import { useEffect } from "react";

/**
 * Alvos do ripple: linhas e controles no padrao do Telegram. O atributo
 * `data-ripple` cobre casos novos sem mexer nesta lista.
 */
const RIPPLE_HOSTS = [
  ".row",
  ".icon-btn",
  ".btn",
  ".sheet-action",
  ".tab",
  ".composer-ic",
  ".swipe-action",
  ".attach-item",
  ".composer-round",
  ".mini-toggle",
  ".chip",
  ".tg-search",
  ".emoji-pop button",
  "[data-ripple]",
].join(",");

/**
 * Ripple do Telegram Web K (src/components/ripple.ts +
 * partials/_ripple.scss): ao tocar/clicar nasce um circulo no ponto do
 * ponteiro que cresce de `scale(0)` a `scale(2)` em 700ms e desaparece.
 *
 * Um unico listener delegado no documento cobre linhas e botoes de toda a
 * app sem custo por componente. Respeita `prefers-reduced-motion` e ignora
 * elementos desabilitados.
 */
export function RippleLayer() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

    const onPointerDown = (event: PointerEvent) => {
      if (reduce.matches) return;
      if (event.pointerType === "mouse" && event.button !== 0) return;
      const target = event.target as HTMLElement | null;
      const host = target?.closest<HTMLElement>(RIPPLE_HOSTS);
      if (!host || host.matches("[disabled], [aria-disabled='true']")) return;

      const rect = host.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return;

      const layer = document.createElement("span");
      layer.className = "c-ripple";
      const circle = document.createElement("span");
      circle.className = "c-ripple__circle";

      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const dx = Math.max(x, rect.width - x);
      const dy = Math.max(y, rect.height - y);
      const size = Math.sqrt(dx * dx + dy * dy) + 2;
      circle.style.width = `${size}px`;
      circle.style.height = `${size}px`;
      circle.style.left = `${x - size / 2}px`;
      circle.style.top = `${y - size / 2}px`;

      layer.appendChild(circle);
      host.appendChild(layer);

      let removed = false;
      const cleanup = () => {
        if (removed) return;
        removed = true;
        layer.remove();
      };
      circle.addEventListener("animationend", cleanup, { once: true });
      window.setTimeout(cleanup, 1200);
    };

    document.addEventListener("pointerdown", onPointerDown, { passive: true });
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  return null;
}
